import React, { useState } from 'react';
import { Badge, Button, Card, Col, Form, Row, Stack } from 'react-bootstrap';
import { Website } from '../types';
import './WebsiteTable.css';

interface Props {
  websites: Website[];
  handleCustomerSelect: (websiteId: number) => void;
  handleSubscriptionSelect: (websiteId: number, pleskCustomerId: number) => void;
  handleBitbucketSelect: (websiteId: number) => void;
  handleInstallBedrock: (repoSlug: string) => void;
  handleCreateProdDb: (websiteId: number, subscriptionId: number) => void;
  handleCreateTestDb: (websiteId: number, subscriptionId: number) => void;
  handleGenerateEnv: (
    subscriptionId: number,
    websiteId: number,
    prodDatabaseName: string,
    prodDatabaseUser: string,
    prodDatabasePass: string
  ) => void;
  handleCommitCircleCIConfig: (websiteId: number) => void;
  handleCreateCircleCIProject: (websiteId: number) => void;
  handleCreateCircleCIEnvVars: (websiteId: number) => void;
  handleInstallWordPress: (websiteId: number, environment: 'prod' | 'test') => void;
  handleSetSubdomain: (websiteId: number) => void;
  handleCreateSubdomain: (websiteId: number) => void;
  handleGenerateEnvForTest: (websiteId: number) => void;
  handleTriggerDeployment: (repoUrl: string, branch: string) => void;
  handleDockerBitbucketSelect: (websiteId: number) => void;
  handleBuildDocker: (websiteId: number) => void;
  handleGenerateAcceptance: (website: Website) => void;
  handleDailyUpdateExclusionChange: (websiteId: number, excluded: boolean) => Promise<void>;
  handleInternalStatusChange: (websiteId: number, isInternal: boolean) => Promise<void>;
}

type DetailItemProps = {
  label: string;
  value: React.ReactNode;
};

const DetailItem: React.FC<DetailItemProps> = ({ label, value }) => (
  <div className="website-detail">
    <span className="website-detail__label">{label}</span>
    <span className="website-detail__value">{value}</span>
  </div>
);

const formatValue = (value: string | number | null | undefined, fallback = 'Not set') => {
  if (value === null || value === undefined || value === '') {
    return fallback;
  }

  return value;
};

const getRepoSlug = (url: string | null) => {
  if (!url) {
    return null;
  }

  return url.replace(/\/$/, '').split('/').pop() ?? null;
};

const acceptanceBadgeVariant = (status: string | null | undefined) => {
  switch ((status ?? '').toLowerCase()) {
    case 'accepted':
    case 'completed':
      return 'success';
    case 'sent':
    case 'delivered':
      return 'primary';
    case 'generated':
      return 'warning';
    case 'cancelled':
    case 'declined':
    case 'voided':
      return 'danger';
    default:
      return 'secondary';
  }
};

const WebsiteTable: React.FC<Props> = ({
  websites,
  handleCustomerSelect,
  handleSubscriptionSelect,
  handleBitbucketSelect,
  handleInstallBedrock,
  handleCreateProdDb,
  handleCreateTestDb,
  handleGenerateEnv,
  handleCommitCircleCIConfig,
  handleCreateCircleCIProject,
  handleCreateCircleCIEnvVars,
  handleInstallWordPress,
  handleSetSubdomain,
  handleCreateSubdomain,
  handleGenerateEnvForTest,
  handleTriggerDeployment,
  handleDockerBitbucketSelect,
  handleBuildDocker,
  handleGenerateAcceptance,
  handleDailyUpdateExclusionChange,
  handleInternalStatusChange,
}) => {
  const [showBasicAuthPasswords, setShowBasicAuthPasswords] = useState(false);
  const [savingDailyUpdateExclusions, setSavingDailyUpdateExclusions] = useState<Set<number>>(new Set());
  const [dailyUpdateErrors, setDailyUpdateErrors] = useState<Record<number, string>>({});
  const [savingInternalStatuses, setSavingInternalStatuses] = useState<Set<number>>(new Set());
  const [internalStatusErrors, setInternalStatusErrors] = useState<Record<number, string>>({});

  const updateDailyUpdateExclusion = async (websiteId: number, excluded: boolean) => {
    setSavingDailyUpdateExclusions(previous => new Set(previous).add(websiteId));
    setDailyUpdateErrors(previous => {
      const next = { ...previous };
      delete next[websiteId];
      return next;
    });

    try {
      await handleDailyUpdateExclusionChange(websiteId, excluded);
    } catch (error) {
      setDailyUpdateErrors(previous => ({
        ...previous,
        [websiteId]: error instanceof Error
          ? error.message
          : 'Failed to update the daily Autocomposer setting.',
      }));
    } finally {
      setSavingDailyUpdateExclusions(previous => {
        const next = new Set(previous);
        next.delete(websiteId);
        return next;
      });
    }
  };

  const updateInternalStatus = async (websiteId: number, isInternal: boolean) => {
    setSavingInternalStatuses(previous => new Set(previous).add(websiteId));
    setInternalStatusErrors(previous => {
      const next = { ...previous };
      delete next[websiteId];
      return next;
    });

    try {
      await handleInternalStatusChange(websiteId, isInternal);
    } catch (error) {
      setInternalStatusErrors(previous => ({
        ...previous,
        [websiteId]: error instanceof Error
          ? error.message
          : 'Failed to update the internal-site setting.',
      }));
    } finally {
      setSavingInternalStatuses(previous => {
        const next = new Set(previous);
        next.delete(websiteId);
        return next;
      });
    }
  };

  const formatBasicAuthPassword = (password: string | null) => {
    if (!password) {
      return 'Not set';
    }

    return showBasicAuthPasswords ? password : 'Hidden';
  };

  if (!websites.length) {
    return (
      <Card className="website-empty-state border-0 shadow-sm">
        <Card.Body>
          <h2>No websites found</h2>
          <p className="mb-0 text-muted">
            Try a different search, or create a new website record to get started.
          </p>
        </Card.Body>
      </Card>
    );
  }

  return (
    <>
      <Form.Check
        id="show-basic-auth-passwords"
        type="switch"
        label="Show basic auth passwords"
        checked={showBasicAuthPasswords}
        onChange={(event) => setShowBasicAuthPasswords(event.target.checked)}
        className="mb-3"
      />

      <Row className="g-4">
      {websites.map((website) => {
        const websiteId = website.id!;
        const subscriptionId = website.pleskSubscription ? Number(website.pleskSubscription) : null;
        const gitRepoSlug = getRepoSlug(website.gitRepoUrl);
        const hasInvalidProdSubscription = Boolean(website.pleskSubscription && website.prodDomainError);
        const canGenerateProdEnv = Boolean(
          subscriptionId &&
          !hasInvalidProdSubscription &&
          website.prodDatabaseName &&
          website.prodDatabaseUser &&
          website.prodDatabasePass
        );
        const canGenerateTestEnv = Boolean(
          website.testDomain &&
          website.testDatabaseName &&
          website.testDatabaseUser &&
          website.testDatabasePass
        );
        const canBuildDocker = Boolean(website.dockerRepoUrl && website.testDatabaseName);
        const isWordPress = website.type === 'wordpress';
        const latestAcceptance = website.latestAcceptance ?? null;
        const canGenerateAcceptance = Boolean(website.testDomain || website.prodDomain || latestAcceptance);
        const acceptanceActionLabel = latestAcceptance ? 'Open Go-Live Acceptance' : 'Generate Go-Live Acceptance';
        const savingDailyUpdateExclusion = savingDailyUpdateExclusions.has(websiteId);
        const savingInternalStatus = savingInternalStatuses.has(websiteId);

        return (
          <Col key={website.id} xs={12}>
            <Card className="website-card h-100 border-0 shadow-sm">
              <Card.Body>
                <div className="website-card__hero">
                  <div>
                    <p className="website-card__eyebrow mb-2">
                      Website #{website.id} · {website.projectCode}
                    </p>
                    <h2 className="website-card__title">{website.name}</h2>
                    <p className="website-card__domain mb-0">
                      {website.prodDomain ?? 'Production domain unavailable'}
                    </p>
                  </div>
                  <Stack direction="horizontal" gap={2} className="website-card__badges flex-wrap">
                    <Badge bg="light" text="dark">{website.type}</Badge>
                    <Badge bg={website.prodDomain ? 'success' : 'secondary'}>
                      {website.prodDomain ? 'Prod linked' : 'Prod unresolved'}
                    </Badge>
                    <Badge bg={website.testDomain ? 'info' : 'secondary'}>
                      {website.testDomain ? 'Test ready' : 'No test domain'}
                    </Badge>
                    <Badge bg={website.gitRepoUrl ? 'warning' : 'secondary'} text={website.gitRepoUrl ? 'dark' : undefined}>
                      {website.gitRepoUrl ? 'Repo linked' : 'Repo missing'}
                    </Badge>
                    {website.excludeFromDailyUpdates && (
                      <Badge bg="warning" text="dark">Daily updates excluded</Badge>
                    )}
                    {website.isInternal && (
                      <Badge bg="dark">Internal · contract not required</Badge>
                    )}
                  </Stack>
                </div>

                <div className="website-autocomposer-setting">
                  <Form.Check
                    id={`internal-site-${websiteId}`}
                    type="switch"
                    label="Internal Gemstone site (customer contract not required)"
                    checked={Boolean(website.isInternal)}
                    disabled={savingInternalStatus}
                    onChange={(event) => updateInternalStatus(websiteId, event.target.checked)}
                  />
                  {savingInternalStatus && (
                    <span className="website-autocomposer-setting__status">Saving…</span>
                  )}
                  {internalStatusErrors[websiteId] && (
                    <div className="website-inline-alert mt-2" role="alert">
                      {internalStatusErrors[websiteId]}
                    </div>
                  )}
                  <Form.Check
                    className="mt-2"
                    id={`exclude-daily-autocomposer-${websiteId}`}
                    type="switch"
                    label="Exclude from daily Autocomposer updates"
                    checked={Boolean(website.excludeFromDailyUpdates)}
                    disabled={savingDailyUpdateExclusion}
                    onChange={(event) => updateDailyUpdateExclusion(websiteId, event.target.checked)}
                  />
                  {savingDailyUpdateExclusion && (
                    <span className="website-autocomposer-setting__status">Saving…</span>
                  )}
                  {dailyUpdateErrors[websiteId] && (
                    <div className="website-inline-alert mt-2" role="alert">
                      {dailyUpdateErrors[websiteId]}
                    </div>
                  )}
                </div>

                <div className="website-summary-grid">
                  <DetailItem label="Production Domain" value={website.prodDomain ?? 'Unavailable'} />
                  <DetailItem label="Test Domain" value={formatValue(website.testDomain, 'Not set')} />
                  <DetailItem label="Plesk Subscription" value={formatValue(website.pleskSubscription, 'Unassigned')} />
                  <DetailItem
                    label="Contract"
                    value={website.isInternal ? 'Not required (internal)' : formatValue(website.contractId, 'Unassigned')}
                  />
                  <DetailItem
                    label="Go-Live Acceptance"
                    value={latestAcceptance ? (
                      <span>
                        <Badge bg={acceptanceBadgeVariant(latestAcceptance.status)} className="me-2">
                          {latestAcceptance.status}
                        </Badge>
                        {latestAcceptance.releaseVersion}
                      </span>
                    ) : 'Not generated'}
                  />
                </div>

                {website.prodDomainError && (
                  <div className="website-inline-alert" role="alert">
                    {website.prodDomainError}
                  </div>
                )}

                <div className="website-section">
                  <div className="website-section__header">
                    <h3>Infrastructure</h3>
                    <p>Plesk ownership, domains, and environment files.</p>
                  </div>
                  <div className="website-detail-grid">
                    <DetailItem label="Customer ID" value={formatValue(website.pleskCustomerId, 'Unassigned')} />
                    <DetailItem label="Subscription ID" value={formatValue(website.pleskSubscription, 'Unassigned')} />
                    <DetailItem label="Test Basic Auth User" value={formatValue(website.testBasicAuthUser, 'Not set')} />
                    <DetailItem label="Test Basic Auth Pass" value={formatBasicAuthPassword(website.testBasicAuthPass)} />
                    <DetailItem
                      label="Env Status"
                      value={canGenerateProdEnv ? 'Ready to generate prod env' : 'Waiting on prod DB details'}
                    />
                  </div>
                  <div className="website-action-row">
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={() => handleCustomerSelect(websiteId)}
                    >
                      Edit Customer
                    </Button>
                    <Button
                      variant="outline-primary"
                      size="sm"
                      disabled={!website.pleskCustomerId}
                      onClick={() => {
                        if (website.pleskCustomerId) {
                          handleSubscriptionSelect(websiteId, website.pleskCustomerId);
                        }
                      }}
                    >
                      Edit Subscription
                    </Button>
                    <Button
                      variant="dark"
                      size="sm"
                      disabled={!canGenerateProdEnv}
                      onClick={() =>
                        handleGenerateEnv(
                          subscriptionId!,
                          websiteId,
                          website.prodDatabaseName!,
                          website.prodDatabaseUser!,
                          website.prodDatabasePass!
                        )
                      }
                    >
                      Generate Env
                    </Button>
                    <Button
                      variant="outline-primary"
                      size="sm"
                      onClick={() => handleSetSubdomain(websiteId)}
                    >
                      Set Subdomain
                    </Button>
                    <Button
                      variant="outline-dark"
                      size="sm"
                      disabled={!website.testDomain}
                      onClick={() => handleCreateSubdomain(websiteId)}
                    >
                      Create in Plesk
                    </Button>
                    <Button
                      variant="secondary"
                      size="sm"
                      disabled={!canGenerateTestEnv}
                      onClick={() => handleGenerateEnvForTest(websiteId)}
                    >
                      Generate Env (Test)
                    </Button>
                  </div>
                </div>

                <div className="website-section">
                  <div className="website-section__header">
                    <h3>Databases</h3>
                    <p>Production and test database credentials and install actions.</p>
                  </div>
                  <div className="website-split-grid">
                    <div className="website-subcard">
                      <h4>Production</h4>
                      <div className="website-detail-grid">
                        <DetailItem label="Name" value={formatValue(website.prodDatabaseName)} />
                        <DetailItem label="User" value={formatValue(website.prodDatabaseUser)} />
                        <DetailItem label="Password" value={formatValue(website.prodDatabasePass)} />
                      </div>
                      <div className="website-action-row">
                        <Button
                          variant="success"
                          size="sm"
                          disabled={!subscriptionId || hasInvalidProdSubscription}
                          onClick={() => handleCreateProdDb(websiteId, subscriptionId!)}
                        >
                          Create Prod DB
                        </Button>
                        {isWordPress && (
                          <Button
                            variant="warning"
                            size="sm"
                            disabled={!subscriptionId || hasInvalidProdSubscription}
                            onClick={() => handleInstallWordPress(websiteId, 'prod')}
                          >
                            Install WP (Prod)
                          </Button>
                        )}
                      </div>
                    </div>

                    <div className="website-subcard">
                      <h4>Test</h4>
                      <div className="website-detail-grid">
                        <DetailItem label="Name" value={formatValue(website.testDatabaseName)} />
                        <DetailItem label="User" value={formatValue(website.testDatabaseUser)} />
                        <DetailItem label="Password" value={formatValue(website.testDatabasePass)} />
                      </div>
                      <div className="website-action-row">
                        <Button
                          variant="info"
                          size="sm"
                          disabled={!subscriptionId}
                          onClick={() => handleCreateTestDb(websiteId, subscriptionId!)}
                        >
                          Create Test DB
                        </Button>
                        {isWordPress && (
                          <Button
                            variant="warning"
                            size="sm"
                            onClick={() => handleInstallWordPress(websiteId, 'test')}
                          >
                            Install WP (Test)
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="website-section">
                  <div className="website-section__header">
                    <h3>Repositories</h3>
                    <p>Bitbucket source, Docker repo, and project automation hooks.</p>
                  </div>
                  <div className="website-split-grid">
                    <div className="website-subcard">
                      <h4>Source Repo</h4>
                      <a
                        className="website-link"
                        href={website.gitRepoUrl ?? undefined}
                        target="_blank"
                        rel="noreferrer"
                      >
                        {website.gitRepoUrl ?? 'No repository linked'}
                      </a>
                      <div className="website-detail-grid mt-3">
                        <DetailItem label="Git User" value={formatValue(website.gitUser, 'Not set')} />
                        <DetailItem label="Git Password" value={formatValue(website.gitPass, 'Not set')} />
                      </div>
                      <div className="website-action-row">
                        <Button
                          variant="primary"
                          size="sm"
                          onClick={() => handleBitbucketSelect(websiteId)}
                        >
                          Edit Repo
                        </Button>
                        {isWordPress && (
                          <Button
                            variant="outline-warning"
                            size="sm"
                            disabled={!gitRepoSlug}
                            onClick={() => handleInstallBedrock(website.gitRepoUrl!)}
                          >
                            Install Bedrock
                          </Button>
                        )}
                      </div>
                    </div>

                    <div className="website-subcard">
                      <h4>Docker Repo</h4>
                      <a
                        className="website-link"
                        href={website.dockerRepoUrl ?? undefined}
                        target="_blank"
                        rel="noreferrer"
                      >
                        {website.dockerRepoUrl ?? 'No Docker repo linked'}
                      </a>
                      <div className="website-action-row mt-3">
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={() => handleDockerBitbucketSelect(websiteId)}
                        >
                          Edit Docker Repo
                        </Button>
                        <Button
                          variant="dark"
                          size="sm"
                          disabled={!canBuildDocker}
                          onClick={() => handleBuildDocker(websiteId)}
                        >
                          Build Docker
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="website-section website-section--delivery">
                  <div className="website-section__header">
                    <h3>Delivery</h3>
                    <p>CircleCI setup and one-click deploy triggers.</p>
                  </div>
                  <div className="website-action-row">
                    <Button
                      variant="secondary"
                      size="sm"
                      disabled={!website.gitRepoUrl}
                      onClick={() => handleCommitCircleCIConfig(websiteId)}
                    >
                      Commit CircleCI Config
                    </Button>
                    <Button
                      variant="info"
                      size="sm"
                      disabled={!website.gitRepoUrl}
                      onClick={() => handleCreateCircleCIProject(websiteId)}
                    >
                      Create CircleCI Project
                    </Button>
                    <Button
                      variant="dark"
                      size="sm"
                      disabled={!website.gitRepoUrl || !website.prodDomain || hasInvalidProdSubscription}
                      onClick={() => handleCreateCircleCIEnvVars(websiteId)}
                    >
                      Set Env Vars
                    </Button>
                    <Button
                      variant="outline-success"
                      size="sm"
                      disabled={!website.gitRepoUrl}
                      onClick={() => handleTriggerDeployment(website.gitRepoUrl!, 'main')}
                    >
                      Deploy Prod (main)
                    </Button>
                    <Button
                      variant="outline-info"
                      size="sm"
                      disabled={!website.gitRepoUrl}
                      onClick={() => handleTriggerDeployment(website.gitRepoUrl!, 'test')}
                    >
                      Deploy Test (test)
                    </Button>
                    <Button
                      variant="success"
                      size="sm"
                      disabled={!canGenerateAcceptance}
                      onClick={() => handleGenerateAcceptance(website)}
                    >
                      {acceptanceActionLabel}
                    </Button>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>
        );
      })}
      </Row>
    </>
  );
};

export default WebsiteTable;
